// DE DÓNDE VIENE LA PLATA — qué parte de la facturación es de tickets, de
// abonados, de convenios.
//
// Tres decisiones que este bloque no puede desandar:
//
// · BARRAS HORIZONTALES, NO UN DONUT. El donut obliga a comparar ángulos, que
//   es justo lo que el ojo hace peor, y en el teléfono se convierte en una
//   rueda de colores con una leyenda abajo que hay que ir y volver a leer. Las
//   barras horizontales llevan el rótulo AL LADO —por eso este bloque no tiene
//   leyenda— y sobreviven a 320 px sin cambiar de forma.
//
// · LAS CUATRO FUENTES PUEDEN SOLAPARSE. Una fila de `cash` puede tener
//   `ticket_id` y `deal_id` a la vez (un ticket cobrado contra un convenio), así
//   que tickets + abonados + convenios puede SUPERAR a `income`. La pantalla
//   vieja esconde el problema sumando convenios adentro de tickets. Acá se
//   detecta, se cambia la base del reparto y se dice en el pie. Lo que nunca se
//   dibuja es una barra negativa ni un total que no cierra sin explicación.
//
// · "CÓMO SE COBRÓ", NO "ESTADÍA". Las cuatro modalidades son la forma de
//   cobro, no cuánto se queda la gente. Un renglón que diga "Estadía: 120"
//   parece contestar la permanencia y contesta otra cosa: el vocabulario actual
//   miente. Por eso el sub-bloque se llama "Cómo se cobró" y las modalidades no
//   llevan tonos distintos, llevan la rampa del azul de Tickets: son la misma
//   familia, subdividida por duración.

import React, { useMemo } from "react";
import Bloque from "./Bloque";
import { UI, colorDe, etiquetaDe } from "./theme";
import { formatCurrency, fecha, mesLargo, variacion, variacionTexto } from "./format";

// El orden de las modalidades es el de la rampa (más corta → más larga), NO el
// del monto. Ordenarlas de mayor a menor dejaría el degradé salteado y la rampa
// deja de leerse sola, que es lo único que aporta sobre cuatro colores
// cualesquiera.
const MODALIDADES = ["hour", "stay", "tn", "plana"];

// Sujetos para redactar la frase. No se derivan de `etiquetaDe` porque las
// etiquetas están escritas para ir al lado de una barra ("Abonados") y no en el
// medio de una oración ("Los abonados aportan…").
const SUJETO = {
  tickets: { conArticulo: "Los tickets", suelto: "tickets" },
  abonados: { conArticulo: "Los abonados", suelto: "abonados" },
  convenios: { conArticulo: "Los convenios", suelto: "convenios" },
  otros: { conArticulo: "Los otros ingresos", suelto: "otros ingresos" },
};

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const sumar = (docs, f) => docs.reduce((a, d) => a + num(f(d)), 0);

/**
 * VENTANA — los documentos del período y los del período previo.
 *
 * Vive acá y no en un `periodo.js` compartido a propósito: mientras el selector
 * de período no exista, este bloque tiene que poder dibujarse solo. El día que
 * el helper compartido llegue, esto se borra y la ventana entra por props.
 */
function ventana(periodo, cerrados, mensuales) {
  if (periodo === "12m") {
    // `mensuales` llega DESCENDENTE de Firestore (useStatsData: orderBy
    // "month","desc") y nadie lo reordena. Sin este sort, slice(-12) traia
    // los doce meses MAS VIEJOS como periodo actual y los mas nuevos como
    // "previa": toda la variacion salia con el signo invertido.
    const asc = [...mensuales].sort((a, b) =>
      String(a.month || "").localeCompare(String(b.month || "")));
    return {
      docs: asc.slice(-12),
      previa: asc.slice(-24, -12),
      granularidad: "mes",
    };
  }
  const n = periodo === "7d" ? 7 : 30;
  return {
    docs: cerrados.slice(-n),
    previa: cerrados.slice(-n * 2, -n),
    granularidad: "dia",
  };
}

/**
 * COMPOSICIÓN — de dónde salió cada peso.
 *
 * `tickets` se suma modalidad por modalidad y NO desde `total.amount`: el
 * sub-bloque necesita el desglose igual, y sumar los cuatro garantiza que la
 * barra grande y el detalle no puedan discrepar.
 *
 * El reparto tiene dos modos, y la diferencia entre uno y otro está medida, no
 * supuesta:
 *
 *   base "income"        el residuo (lo que `income` tiene de más) es plata real
 *                        cobrada que no cayó en ninguna categoría → va a "Otros"
 *                        junto con los movimientos manuales. Todo cierra contra
 *                        `income`, que es el número que el dueño ya conoce.
 *
 *   base "identificado"  las categorías se solapan y su suma supera a `income`.
 *                        Restar daría un "Otros" NEGATIVO, así que se cambia el
 *                        denominador: los porcentajes pasan a ser sobre lo
 *                        identificado y el pie lo aclara. Preferimos un total
 *                        distinto de `income` explicado, a un número que cierre
 *                        por arte de magia.
 *
 * El 2% de tolerancia existe porque redondeos y cobros a caballo de la
 * medianoche mueven el residuo unos pesos: sin ese margen el bloque cambiaría de
 * modo un día sí y otro no, y el pie aparecería y desaparecería sin motivo.
 */
function composicion(docs) {
  const income = sumar(docs, (d) => d.income);
  const tickets = sumar(docs, (d) =>
    MODALIDADES.reduce((a, k) => a + num(d && d.tickets && d.tickets[k] && d.tickets[k].amount), 0));
  const abonados = sumar(docs, (d) => d && d.monthly && d.monthly.amount);
  const convenios = sumar(docs, (d) => d && d.deals && d.deals.amount);
  const manual = sumar(docs, (d) => d && d.manual && d.manual.amount);

  const residuo = income - (tickets + abonados + convenios + manual);
  const solapamiento = residuo < -(income * 0.02);

  // En modo "identificado" el residuo se descarta —es la prueba del solapamiento,
  // no plata—, pero `manual` se mantiene: son cobros con TODAS las claves
  // foráneas en null, o sea que por construcción no pueden estar contados dos
  // veces. Tirarlos sería esconder plata real para respetar una regla que se
  // escribió contra el residuo.
  const otros = solapamiento ? manual : manual + Math.max(0, residuo);

  const partes = [
    { clave: "tickets", monto: tickets },
    { clave: "abonados", monto: abonados },
    { clave: "convenios", monto: convenios },
    { clave: "otros", monto: otros },
  ].filter((p) => p.monto > 0);

  const total = solapamiento ? partes.reduce((a, p) => a + p.monto, 0) : income;

  return {
    base: solapamiento ? "identificado" : "income",
    solapamiento,
    total,
    income,
    tickets,
    partes: partes.sort((a, b) => b.monto - a.monto),
    modalidades: MODALIDADES.map((k) => ({
      clave: k,
      monto: sumar(docs, (d) => d && d.tickets && d.tickets[k] && d.tickets[k].amount),
    })).filter((m) => m.monto > 0),
  };
}

/**
 * CATEGORÍAS — una línea de texto, no un bloque.
 *
 * Se cuenta por VEHÍCULOS que entraron y no por plata: la pregunta que contesta
 * es "qué entra a mi playa", y en plata una moto que se queda ocho horas puede
 * pesar más que un auto de veinte minutos y mezclar las dos lecturas.
 */
function fraseCategorias(docs) {
  const acum = {};
  docs.forEach((d) => {
    const cats = d && d.categories;
    if (!cats || typeof cats !== "object") return;
    Object.keys(cats).forEach((k) => {
      acum[k] = (acum[k] || 0) + num(cats[k] && cats[k].in);
    });
  });

  const orden = Object.keys(acum)
    .map((nombre) => ({ nombre, n: acum[nombre] }))
    .filter((c) => c.n > 0)
    .sort((a, b) => b.n - a.n);

  const total = orden.reduce((a, c) => a + c.n, 0);
  if (!total || !orden.length) return null;

  if (orden.length === 1) {
    return `Todos los vehículos que entran son de la categoría ${orden[0].nombre}.`;
  }

  const d1 = Math.round((orden[0].n / total) * 10);
  const d2 = Math.round((orden[1].n / total) * 10);

  // El nombre de la categoría lo escribe el cliente, en su idioma y con su
  // ortografía ("Auto", "Camión", "Bici"). No se pluraliza ni se pasa a
  // minúscula: "7 son camións" es peor que cualquier ganancia de estilo, así que
  // la oración se arma alrededor del nombre tal como vino.
  if (d2 < 1) return `De cada 10 vehículos, ${d1} entran como ${orden[0].nombre}.`;
  return `De cada 10 vehículos, ${d1} entran como ${orden[0].nombre} y ${d2} como ${orden[1].nombre}.`;
}

/**
 * Cuántos meses representa la ventana, para poder decir "al mes" sin mentir.
 * Con 12 mensuales se divide por la cantidad de meses; con 30 diarios la
 * ventana YA es un mes; con 7 se dice "en la semana" y no se extrapola.
 */
function cadencia(granularidad, periodo, nDocs) {
  if (granularidad === "mes") return { texto: "por mes en promedio", divisor: Math.max(nDocs, 1) };
  if (periodo === "7d") return { texto: "en la semana", divisor: 1 };
  return { texto: "al mes", divisor: 1 };
}

/**
 * LA FRASE — la conclusión y el segundo renglón.
 *
 * Regla de la pantalla: ningún número aparece solo. Si esta función no puede
 * armar un titular, el bloque muestra su estado vacío en vez de dibujar barras
 * que nadie sabría cómo leer.
 */
function redactar(comp, previa, cad) {
  if (!comp.partes.length || comp.total <= 0) return null;

  const top = comp.partes[0];
  const pctTop = Math.round((top.monto / comp.total) * 100);
  const sujeto = SUJETO[top.clave];

  const titular = pctTop >= 97
    ? `Todo lo que entra viene de ${sujeto.suelto}`
    : `El ${pctTop}% de lo que entra viene de ${sujeto.suelto}`;

  const segunda = comp.partes[1];
  if (!segunda) {
    return {
      titular,
      frase: "Es tu única fuente de ingresos en este período: todo lo que factures depende del movimiento diario.",
    };
  }

  const s2 = SUJETO[segunda.clave];

  // La tendencia sólo se afirma con período previo Y con una diferencia grande.
  // `variacion` devuelve null cuando no hay con qué comparar, que no es lo mismo
  // que 0%: decir "se mantiene" sin período anterior sería afirmar algo que no
  // se midió.
  const previo = previa ? previa[segunda.clave] : null;
  const v = variacion(segunda.monto, previo);
  let cola = "";
  if (v !== null) {
    if (v >= 10) cola = " y vienen creciendo";
    else if (v <= -10) cola = " y vienen cayendo";
    else cola = " y se mantienen parejos";
  }

  return {
    titular,
    frase: `${s2.conArticulo} aportan ${formatCurrency(segunda.monto / cad.divisor)} ${cad.texto}${cola}.`,
  };
}

// ── Una fila: rótulo · barra · monto · variación ─────────────────────────
//
// El monto va AFUERA del extremo de la barra, en su propia columna. Adentro
// tendría que achicarse cuando la barra es corta y desaparecer cuando es mínima,
// justo en el caso en que más falta hace leerlo.
const Fila = ({ rotulo, color, monto, share, nota, variacionTxt }) => (
  <div className="d-flex align-items-center" style={{ gap: 8, minHeight: 32 }}>
    <div
      style={{
        flex: "0 0 78px",
        fontSize: 12.5,
        color: UI.texto,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
      title={rotulo}
    >
      {rotulo}
    </div>

    <div
      style={{
        flex: "1 1 auto",
        minWidth: 32,
        height: 14,
        background: UI.fondoSuave,
        borderRadius: 7,
        overflow: "hidden",
      }}
    >
      {/* La barra es decorativa: el rótulo, el monto y el porcentaje ya están
          en texto acá al lado, así que un lector de pantalla no se pierde nada. */}
      <div
        aria-hidden="true"
        style={{
          width: `${Math.max(0, Math.min(100, share * 100))}%`,
          minWidth: share > 0 ? 3 : 0,
          height: "100%",
          background: color,
          borderRadius: 7,
        }}
      />
    </div>

    <div style={{ flex: "0 0 auto", textAlign: "right", minWidth: 84 }}>
      <div style={{ fontSize: 13.5, fontWeight: 700, color: UI.texto, lineHeight: 1.2 }}>
        {formatCurrency(monto)}
      </div>
      <div style={{ fontSize: 11, color: UI.textoSuave, lineHeight: 1.2 }}>{nota}</div>
    </div>

    {/* La variación va en gris, no en verde/rojo: acá el color ya está ocupado
        por la identidad del origen, y meter POLARIDAD en la misma fila haría
        que dos bancos de color compitan por el mismo renglón. */}
    <div style={{ flex: "0 0 56px", textAlign: "right", fontSize: 11.5, color: UI.textoSuave }}>
      {variacionTxt}
    </div>
  </div>
);

const DeDondeVieneLaPlata = ({ cerrados = [], mensuales = [], periodo = "30d" }) => {
  const datos = useMemo(() => {
    const v = ventana(periodo, cerrados, mensuales);
    if (!v.docs.length) return null;

    const comp = composicion(v.docs);
    const compPrevia = v.previa.length ? composicion(v.previa) : null;

    // Índice de la ventana previa por concepto, para poder preguntar "cuánto fue
    // esto mismo el período pasado" sin recorrer los documentos otra vez.
    const previa = compPrevia
      ? compPrevia.partes.reduce((a, p) => Object.assign(a, { [p.clave]: p.monto }), {})
      : null;

    const cad = cadencia(v.granularidad, periodo, v.docs.length);
    const texto = redactar(comp, previa, cad);
    if (!texto) return null;

    // Días CON MOVIMIENTO, no días de calendario. Un local que cerró seis días no
    // tuvo "30 días": tuvo 24. Y contarle los otros seis como $0 le hunde todos
    // los promedios de la pantalla justo por haber estado cerrado.
    const conMovimiento = v.docs.filter(
      (d) => num(d.income) > 0 || num(d && d.tickets && d.tickets.total && d.tickets.total.in) > 0
    ).length;

    const primero = v.docs[0];
    const ultimo = v.docs[v.docs.length - 1];
    const rango = v.granularidad === "mes"
      ? `${mesLargo(primero.month)} – ${mesLargo(ultimo.month)} · ${v.docs.length} ${v.docs.length === 1 ? "mes" : "meses"}`
      : `${fecha(primero.date)} – ${fecha(ultimo.date)} · ${conMovimiento} ${conMovimiento === 1 ? "día" : "días"} con movimiento`;

    return { comp, previa, texto, rango, categorias: fraseCategorias(v.docs) };
  }, [cerrados, mensuales, periodo]);

  // Sin frase no hay bloque: se dice qué falta y cómo se consigue, en vez de
  // dibujar cuatro barras en cero que no permiten concluir nada.
  if (!datos) {
    return (
      <Bloque
        nombre="De dónde viene la plata"
        id="de-donde-viene-la-plata"
        titular="Todavía no se puede ver de dónde viene la plata"
        frase="No hay cobros registrados en este período. En cuanto se cobre el primer ticket, el reparto entre tickets, abonados y convenios aparece solo."
      />
    );
  }

  const { comp, previa, texto, rango, categorias } = datos;

  const variacionDe = (clave, monto) => {
    if (!previa) return "";
    const v = variacion(monto, previa[clave]);
    return v === null ? "" : variacionTexto(v);
  };

  // ── El plegable: cómo se cobró ────────────────────────────────────────
  const detalle = comp.modalidades.length ? (
    <div>
      <div style={{ fontSize: 12.5, color: UI.textoSuave, marginBottom: 8, lineHeight: 1.45 }}>
        Cómo se cobraron los {formatCurrency(comp.tickets)} de tickets. No es cuánto se queda
        la gente: es la modalidad con la que se le cobró.
      </div>

      {comp.modalidades.map((m) => (
        <Fila
          key={m.clave}
          rotulo={etiquetaDe("modalidad", m.clave)}
          color={colorDe("modalidad", m.clave)}
          monto={m.monto}
          share={comp.tickets > 0 ? m.monto / comp.tickets : 0}
          nota={`${comp.tickets > 0 ? Math.round((m.monto / comp.tickets) * 100) : 0}% de los tickets`}
          variacionTxt=""
        />
      ))}

      {categorias && (
        <div
          style={{
            fontSize: 12.5,
            color: UI.texto,
            marginTop: 10,
            paddingTop: 10,
            borderTop: `1px solid ${UI.linea}`,
            lineHeight: 1.45,
          }}
        >
          {categorias}
        </div>
      )}
    </div>
  ) : null;

  return (
    <Bloque
      nombre="De dónde viene la plata"
      id="de-donde-viene-la-plata"
      titular={texto.titular}
      frase={texto.frase}
      rango={rango}
      acento={colorDe("origen", comp.partes[0].clave)}
      detalle={detalle}
      detalleLabel="Cómo se cobró"
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {comp.partes.map((p) => (
          <Fila
            key={p.clave}
            rotulo={etiquetaDe("origen", p.clave)}
            color={colorDe("origen", p.clave)}
            monto={p.monto}
            share={comp.total > 0 ? p.monto / comp.total : 0}
            nota={`${comp.total > 0 ? Math.round((p.monto / comp.total) * 100) : 0}% del total`}
            variacionTxt={variacionDe(p.clave, p.monto)}
          />
        ))}
      </div>

      <div style={{ fontSize: 11.5, color: UI.textoSuave, marginTop: 10, lineHeight: 1.45 }}>
        {comp.solapamiento ? (
          // Sin esta línea el dueño suma las barras, le da distinto a lo que ve
          // en Caja, y deja de creerle a la pantalla entera.
          <>
            Sobre lo que se pudo identificar. Hay cobros que entran en más de una categoría
            (por ejemplo un ticket cobrado por convenio).
          </>
        ) : (
          <>Total del período: {formatCurrency(comp.total)}.</>
        )}
        {previa && <> La columna de la derecha es la variación contra el período anterior.</>}
      </div>
    </Bloque>
  );
};

export default DeDondeVieneLaPlata;
